// ─────────────────────────────────────────────────────────────────────────────
// THE ACCOUNT CONTROLS — Settings, Log Out, and the compliance footer.
//
// ⚠️ ONE IMPLEMENTATION, TWO HOSTS. This lived inline in AppShell, which meant
// logout existed only on the 14 authenticated routes AppShell wraps. /apply —
// the pages where a borrower enters SSN, income and employment — renders its own
// header and therefore had NO WAY TO LOG OUT AT ALL. Not hidden behind a
// breakpoint: absent. On a shared device that is a real problem, not a cosmetic
// one.
//
// The fix is not a second menu in the application header. A second menu is a
// second thing to drift — the copy diverges, one gains an item the other
// doesn't, and eventually only one of them clears push listeners on the way out.
// Both hosts render THIS.
//
// ⚠️ LOGOUT LIVES HERE, NOT IN THE HOSTS. useAuth().logout() tears down native
// push listeners before clearing the session; a host that reimplemented the
// button would sooner or later skip that.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLoanNumber } from '../hooks/useLoanNumber';
import { buildLine } from '../constants/build';

const rowClass = (active) =>
  `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
    active ? 'bg-[#3a6ad9]/10 text-[#3a6ad9]' : 'text-gray-500 hover:bg-gray-100 hover:text-black'
  }`;

/**
 * @param onNavigate  called before any navigation — hosts use it to close their drawer/sheet
 * @param saveAndExit optional async () => void. When supplied, renders
 *                    "Save & continue later" ABOVE the other items. Only pass
 *                    this from a surface that genuinely persists progress.
 */
export default function AccountActions({ onNavigate = () => {}, saveAndExit = null }) {
  const { logout } = useAuth();
  const { loanNumber, isJoint } = useLoanNumber();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);

  const handleLogout = () => {
    onNavigate();
    logout();
    navigate('/login');
  };

  // ⚠️ AWAITS THE SAVE BEFORE LEAVING, AND SAYS SO WHILE IT WAITS. The
  // application autosaves on a one-second debounce, so navigating immediately
  // would drop the last keystrokes — and this control's copy promises they are
  // kept. The host passes a flush that clears the pending timer and POSTs now.
  const handleSaveAndExit = async () => {
    if (saving) return;
    setSaving(true);
    try { await saveAndExit(); } catch (_) { /* the save is best-effort; leaving must still work */ }
    setSaving(false);
    onNavigate();
    navigate('/dashboard');
  };

  return (
    <div className="px-3 py-4 border-t border-gray-100 flex-shrink-0">
      {saveAndExit && (
        <button onClick={handleSaveAndExit} disabled={saving} className={rowClass(false)}>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-4 4m0 0l-4-4m4 4V3" />
          </svg>
          {saving ? 'Saving…' : 'Save & continue later'}
        </button>
      )}
      <Link to="/settings" onClick={onNavigate} className={rowClass(location.pathname === '/settings')}>
        <svg className={`w-5 h-5 ${location.pathname === '/settings' ? 'text-[#3a6ad9]' : 'text-gray-400'}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Settings
      </Link>
      <button onClick={handleLogout} className={rowClass(false)}>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Log Out
      </button>
      {/* Compliance footer travels with the controls — it was inline in AppShell
          and would otherwise appear on the dashboard and not on the form. */}
      <div style={{ padding: '10px 12px 0', marginTop: 8, borderTop: '1px solid #f3f4f6' }}>
        <div style={{ fontSize: 10, color: '#d1d5db', lineHeight: 1.7 }}>
          <div>Go Doorly, LLC</div>
          <div>NMLS #2764749</div>
          <div>Equal Housing Opportunity</div>
          <a href="mailto:support@godoorly.com" style={{ color: '#d1d5db', textDecoration: 'none' }}>support@godoorly.com</a>
          {/* ⚠️ THE FILE REFERENCE, WHERE SUPPORT ASKS FOR IT. Sits beside the
              build stamp for the same reason that does: this is the screen a
              person is already on when they are told "read me your loan
              number", and neither string is discoverable anywhere else on a
              phone. Absent until a number is assigned — a lead who has not been
              through underwriting has none, and rendering "Loan #: —" invites
              someone to quote a dash.

              ⚠️ ONE NUMBER, TWO PEOPLE, SAID OUT LOUD. loan_number identifies a
              FILE, not a person: a co-borrower deliberately inherits the
              primary's number, so two users legitimately share one. Telling the
              borrower it is shared is what stops "why does my loan have someone
              else's name on it" arriving as a support ticket — and it warns
              staff, via the borrower, that a lookup returns two rows. */}
          {loanNumber && (
            <div style={{ marginTop: 6, fontFamily: 'ui-monospace, monospace', color: '#9ca3af' }}
              title={isJoint
                ? 'Your loan file reference — shared with your co-borrower'
                : 'Your loan file reference — quote this when you contact support'}>
              loan {loanNumber}
              {isJoint && (
                <span style={{ fontFamily: 'inherit', color: '#d1d5db' }}> · shared with your co-borrower</span>
              )}
            </div>
          )}
          {/* ⚠️ THE BUILD, WHERE A PERSON CAN READ IT WITHOUT DEVTOOLS. The
              backend has answered "which commit?" at /api/health all along; the
              web layer could not, so a frontend fix was repeatedly confirmed
              against a build that did not contain it. This is the on-device
              answer: whoever is looking at the bug can read the build off the
              same screen, on the same phone, with no tooling.

              ⚠️ SOURCE AS WELL AS SHA. One commit produces a Vercel build, a
              Capgo OTA bundle and a baked native bundle; a native app can be
              running any of them, which is precisely the question that could not
              be answered when a fix "wasn't on the phone". */}
          <div style={{ marginTop: 6, fontFamily: 'ui-monospace, monospace', color: '#e5e7eb' }}
            title="Build this screen is running">
            build {buildLine()}
          </div>
        </div>
      </div>
    </div>
  );
}
